/**
 * Gemeinsame Typen der Fachlogik.
 *
 * Zwei Sprachen treffen hier aufeinander, und das ist Absicht (CLAUDE.md):
 *   - Was aus der Datenbank kommt, heisst wie dort: englisch, Spalte fuer
 *     Spalte (`price_net`, `from_day`, `min_charge_net`, `included_units`).
 *   - Was die Fachlogik selbst benennt, heisst deutsch (`Miettage`, `Lage`,
 *     `Zuschlagszeile`).
 *   - Aufzaehlungswerte sind deutsch (`'tagesstaffel'`, `'geht'`), weil sie
 *     Fachbegriffe sind und keine Technik.
 *
 * Sichtbarer Text kommt hier nie vor. Wo die Oberflaeche etwas anzeigen soll,
 * steht ein i18n-Schluessel mit den Zahlen daneben.
 */
// ── Datum ───────────────────────────────────────────────────────────
//
// Alle Datumsangaben werden nach ihren UTC-Bestandteilen gelesen. Ein
// `date` aus Postgres kommt als '2026-05-04' an, und `new Date('2026-05-04')`
// ist UTC-Mitternacht. Wer stattdessen `new Date(2026, 4, 4)` uebergibt,
// liefert Ortszeit und verschiebt sich westlich von Greenwich um einen Tag.
// Deshalb gibt es `tag()` und `ausIso()` -- benutzt die, nicht den Konstruktor.
/** Ein Kalendertag als UTC-Mitternacht. Monat ist 1-basiert, wie im Gespraech. */
export function tag(jahr, monat, tagImMonat) {
    return new Date(Date.UTC(jahr, monat - 1, tagImMonat));
}
/** '2026-05-04' -> UTC-Mitternacht. Alles hinter dem Tag wird abgeschnitten. */
export function ausIso(datum) {
    const teile = datum.slice(0, 10).split('-');
    const jahr = Number(teile[0]);
    const monat = Number(teile[1]);
    const tagImMonat = Number(teile[2]);
    return new Date(Date.UTC(jahr, monat - 1, tagImMonat));
}
/**
 * Kalendertag -> '2026-05-04'. Fuer `rand` und fuer Schritte.
 *
 * **In deutscher Zeit** (N7-07, 05.10.2026): Eine UTC-Mitternacht bleibt ihr
 * Tag; ein echter Zeitpunkt aus dem Ist (Übergabe 05.10. 00:03 = 04.10.
 * 22:03 UTC) ist der Tag in Deutschland, nicht der in UTC. Dieselbe
 * Umrechnung wie `tagNummer` (kalender.ts), hier ohne Import, damit
 * `typen.ts` unten in der Reihenfolge bleibt.
 */
export function alsIso(datum) {
    const t = datum.getTime();
    const jahr = datum.getUTCFullYear();
    const umstellung = (monat0) => {
        const letzter = new Date(Date.UTC(jahr, monat0 + 1, 0));
        return Date.UTC(jahr, monat0, letzter.getUTCDate() - letzter.getUTCDay(), 1);
    };
    const versatz = t >= umstellung(2) && t < umstellung(9) ? 7_200_000 : 3_600_000;
    return new Date(t + versatz).toISOString().slice(0, 10);
}
// ── Geld ────────────────────────────────────────────────────────────
/**
 * Kaufmaennisch auf zwei Nachkommastellen (halbe Cents weg von der Null).
 *
 * Gerundet wird auf Positionsebene, danach wird summiert -- nie umgekehrt.
 * Wer die Summe rundet und rueckwaerts verteilt, weicht um Cents von Lexware
 * ab, und jemand sucht einen Nachmittag lang danach (04-preisfindung.md).
 *
 * Das `1e-9` faengt den Fall ab, dass 0,145 binaer knapp unter 14,5 liegt.
 */
export function geld(betrag) {
    if (!Number.isFinite(betrag))
        return 0;
    const skaliert = betrag * 100;
    const vorzeichen = skaliert < 0 ? -1 : 1;
    return (vorzeichen * Math.round(Math.abs(skaliert) + 1e-9)) / 100;
}
// ── Die Sammlung ────────────────────────────────────────────────────
/**
 * Wie lange eine Sammlung gilt -- die Merkliste des Innendiensts.
 *
 * Zwoelf Stunden, seit sie im Browser lebte: *"Ein Posten von gestern ist
 * kein Warenkorb, sondern ein Fund -- und ein Fund, den man versehentlich
 * vermietet, ist ein Auftrag ueber den falschen Zeitraum."* Seit Migration
 * 0134 haengt die Sammlung am Mitarbeiter und nicht am Geraet, und damit
 * zaehlt dieselbe Frist fuer alle Geraete zusammen.
 *
 * **Die Zahl steht hier und sonst nirgends.** Der Browser rechnet sie in
 * Millisekunden um (`HAELT_MS` in apps/web/src/daten/merkliste.ts), der
 * Server misst daran das Alter der Zeile (apps/api/src/routen/merkliste.ts).
 * Zwei Fassungen waeren zwei Alter fuer eine Sammlung.
 */
export const SAMMLUNG_HAELT_STUNDEN = 12;
//# sourceMappingURL=typen.js.map